import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { graphql, useFragment, usePreloadedQuery } from 'react-relay';
import { isLoaderErrorAttributes } from '@jira/platform__react-loosely-lazy-error-handling/src/is-loader-error-attributes.tsx';
import { Box, xcss } from '@atlaskit/primitives';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { FieldOption } from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import { JQLBuilderErrorBoundryFallback } from '@atlassian/jira-jql-builder-common/src/ui/error-boundry-fallback/index.tsx';
import { HYDRATION_QUERY } from '@atlassian/jira-jql-builder-common/src/ui/hydrate-jql-builder/useHydrationQueryLoader.tsx';
import { JQL_BUILDER_PERFORMANCE_MARKS } from '@atlassian/jira-jql-builder-common/src/ui/performance-context/constants.tsx';
import { usePerformanceContext } from '@atlassian/jira-jql-builder-common/src/ui/performance-context/index.tsx';
import {
	ContextualAnalyticsData,
	useAnalyticsEvents,
	type AnalyticsAttributes,
} from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useTextSearchInputClause } from '@atlassian/jira-jql-utils/src/utils/text-search-input-clause/use-text-search-input-clause.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { useHydrationQueryLoaderQuery } from '@atlassian/jira-jql-builder-common/src/ui/hydrate-jql-builder/__generated__/useHydrationQueryLoaderQuery.graphql';
import {
	PACKAGE_NAME,
	TEAM_NAME,
	getTextSearchInputField,
	jqlAnonymizerVisitor,
} from '../../common/constants.tsx';
import {
	GenericPropTypes,
	PickerTypes,
	type FieldToRender,
	type OnSearchWrapper,
} from '../../common/types.tsx';
import { JQLEditorLoading } from '../../common/ui/jql-editor-loading/index.tsx';
import ListItemChild from '../../common/ui/list-item-child/index.tsx';
import { StyledChild, StyledListItemChild } from '../../common/ui/styled.tsx';
import type { JQLEditorUIOldProps, JQLEditorUIProps } from '../../common/ui/types.tsx';
import { ActivePopupKeyContainer } from '../../controllers/active-popup/index.tsx';
import {
	useAST,
	useTextSearchInputClause as useTextSearchInputClauseOld,
} from '../../controllers/ast/index.tsx';
import { useIntegrateDotDialogContext } from '../../controllers/dot-dialog-context/index.tsx';
import { useSearchState } from '../../controllers/search-state/index.tsx';
import { gatherFields } from '../../utils/gather-fields/main.tsx';
import { getAstSortFields } from '../../utils/get-ast-sort-fields/index.tsx';
import { removeOptionalFieldsFromJql } from '../../utils/remove-optional-fields-from-jql/index.tsx';
import { useVerticalMode } from '../../controllers/feature-context.tsx';
import { JQLBuilderContainer } from '../../common/ui/JQLBuilderContainer.tsx';
import type {
	JQLEditorUIWithErrorBoundary$key,
	JQLEditorUIWithErrorBoundary$data,
} from './__generated__/JQLEditorUIWithErrorBoundary.graphql';
import { AddFilter } from './add-filter/index.tsx';
import { ResetButton } from './reset-button/index.tsx';
import Field from './field/main.tsx';
import OrderByPicker from './order-by-picker/index.tsx';
import { AsyncRegisterSearchCriteriaToShortcutsDialog } from './register-search-criteria-to-shortcuts-dialog/async.tsx';
import { TextSearchInput } from './text-search-input/index.tsx';
import { useFieldsToRender } from './use-fields-to-render/index.tsx';

/* eslint-disable @atlassian/relay/unused-fields, @atlassian/relay/graphql-naming  */
const JQL_EDITOR_HYDRATION_FRAGMENT = graphql`
	fragment JQLEditorUIWithErrorBoundary on Query {
		jira @include(if: $includeJira) {
			jqlBuilder(cloudId: $cloudId) {
				hydrateJqlQuery(query: $jql, input: $input, viewContext: $viewContext, scope: $scope)
					@skip(if: $isFilter) {
					... on JiraJqlHydratedQuery {
						fields {
							... on JiraJqlQueryHydratedField {
								jqlTerm
								field {
									displayName
									searchTemplate {
										key
									}
									shouldShowInContext
									shouldExcludeInScope(scope: $scope)
										@include(if: $isPlanReleaseFeatureToggleEnabled)
									type
									description
									operators
									fieldType {
										displayName
									}
								}
							}
						}
					}
				}
				hydrateJqlQueryForFilter(id: $filterAri, viewContext: $viewContext, scope: $scope)
					@include(if: $isFilter) {
					... on JiraJqlHydratedQuery {
						fields {
							... on JiraJqlQueryHydratedField {
								jqlTerm
								field {
									displayName
									searchTemplate {
										key
									}
									shouldShowInContext
									shouldExcludeInScope(scope: $scope)
										@include(if: $isPlanReleaseFeatureToggleEnabled)
									type
									description
									operators
									fieldType {
										displayName
									}
								}
							}
						}
					}
				}
			}
		}
	}
`;
/* eslint-enable @atlassian/relay/unused-fields, @atlassian/relay/graphql-naming */

const JQLEditorUIOld = ({
	query,
	onSearch,
	onSwitchToJql,
	excludedFields = [],
	onReady,
	onJourneyStart,
	NoOptionComponent,
	hydrationQueryRef,
	isHydrating,
	projectOptions,
	singleSelectFields,
	shouldUseListMarkup,
	hideOperatorDropdown = false,
	irremovableFields,
	hideTextSearchInput = false,
	searchPlaceholder,
	showOrderByPicker = false,
	contextJql,
	viewContext,
	scope = null,
	suggestedUsersCount,
}: JQLEditorUIOldProps) => {
	const onPerformanceMark = usePerformanceContext();

	useLayoutEffect(() => {
		onPerformanceMark({
			mark: JQL_BUILDER_PERFORMANCE_MARKS.JQL_BUILDER_BASIC_JS_END,
		});
	}, [onPerformanceMark]);

	// eslint-disable-next-line @atlassian/relay/query-restriction
	const hydratedData = usePreloadedQuery<useHydrationQueryLoaderQuery>(
		HYDRATION_QUERY,
		hydrationQueryRef,
	);

	const fieldsHydrationData: JQLEditorUIWithErrorBoundary$data =
		useFragment<JQLEditorUIWithErrorBoundary$key>(JQL_EDITOR_HYDRATION_FRAGMENT, hydratedData);

	const loading = isHydrating ?? false;

	useLayoutEffect(() => {
		if (loading === false) {
			onReady && onReady();
		}
	}, [loading, onReady]);

	const intl = useIntl();
	const [ast] = useAST();
	const [textSearchInputClauseOld] = useTextSearchInputClauseOld();
	const textSearchInputClauseNew = useTextSearchInputClause(ast, getTextSearchInputField());

	const textSearchInputClause = fg('shplxi_628_cleanup')
		? textSearchInputClauseNew
		: textSearchInputClauseOld;

	const [{ astClauseOrder, clausesByFieldId, secondaryClausesByFieldId }] = useSearchState();
	const { isShortcutsDialogIntegrationAvailable } = useIntegrateDotDialogContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { internalState, setInternalState } = useFieldsToRender({
		query,
		loading,
		fieldsHydrationData,
		excludedFields,
		irremovableFields,
	});

	const onSearchWrapper: OnSearchWrapper = useCallback(
		(jql) => {
			if (onSearch) {
				setInternalState({
					internalJql: jql,
				});
				onSearch(jql);
			}
		},
		[onSearch, setInternalState],
	);

	const onFieldsUpdate = useCallback(
		({
			fieldsToRender,
			selectedFields,
		}: {
			fieldsToRender?: FieldToRender[];
			selectedFields?: FieldOption[];
		}) => {
			if (fieldsToRender !== undefined || selectedFields !== undefined) {
				setInternalState({
					...(fieldsToRender !== undefined ? { fieldsToRender } : {}),
					...(selectedFields !== undefined ? { selectedFields } : {}),
				});
			}
		},
		[setInternalState],
	);

	// Required fields are those that are never modified by the More dropdown (AddFilter component).
	// The More dropdown only manages optional fields.
	// Required fields:
	// - Cannot be added from the More dropdown
	// - Cannot be cleared from the More dropdown, if they are in the query, they remain in the query
	const requiredFields = useMemo(
		() => new Set<string>([...irremovableFields.keys(), ...(excludedFields ?? [])]),
		[excludedFields, irremovableFields],
	);

	const onClearAddFilterValues = useCallback(() => {
		const { fieldsToRender, optionalFieldsCount } = gatherFields({
			selectedFields: [],
			excludedFields,
			intl,
			fieldsHydratedData: fieldsHydrationData,
			irremovableFields,
			astClauseOrder,
			clausesByFieldId,
			secondaryClausesByFieldId,
		});

		setInternalState({
			selectedFields: [],
			fieldsToRender,
			optionalFieldsCount,
		});

		onSearchWrapper(removeOptionalFieldsFromJql(ast, requiredFields, textSearchInputClause));
	}, [
		ast,
		excludedFields,
		intl,
		fieldsHydrationData,
		irremovableFields,
		astClauseOrder,
		clausesByFieldId,
		secondaryClausesByFieldId,
		setInternalState,
		onSearchWrapper,
		requiredFields,
		textSearchInputClause,
	]);

	const { showLoadingState, fieldsToRender, selectedFields, optionalFieldsCount } = internalState;

	const StyledListItemChildComponent = isVisualRefreshEnabled()
		? ListItemChild
		: StyledListItemChild;

	const FieldWrapper = shouldUseListMarkup ? StyledListItemChildComponent : StyledChild;

	const orderByFieldCount = useMemo(
		() => (showOrderByPicker ? getAstSortFields(ast).length : undefined),
		[ast, showOrderByPicker],
	);

	return (
		<JQLBuilderContainer>
			{showLoadingState ? (
				<JQLEditorLoading
					fieldsToRender={fieldsToRender}
					optionalFieldsCount={optionalFieldsCount}
					shouldUseListMarkup={shouldUseListMarkup}
					hideTextSearchInput={hideTextSearchInput}
					searchPlaceholder={searchPlaceholder}
					showOrderByPicker={showOrderByPicker}
					orderByFieldsCount={orderByFieldCount}
				/>
			) : (
				<ActivePopupKeyContainer
					createAnalyticsEvent={createAnalyticsEvent}
					onJourneyStart={onJourneyStart}
				>
					{!hideTextSearchInput && (
						<ContextualAnalyticsData
							attributes={{
								fieldType: getTextSearchInputField(),
								searchTemplate: getTextSearchInputField(),
								refinementType: PickerTypes.Text,
								propType: GenericPropTypes.NoProp,
							}}
						>
							<TextSearchInput
								onSearchWrapper={onSearchWrapper}
								onJourneyStart={onJourneyStart}
								shouldUseListMarkup={shouldUseListMarkup}
								placeholder={searchPlaceholder}
							/>
						</ContextualAnalyticsData>
					)}
					{fieldsToRender.map((fieldToRender) => (
						<ContextualAnalyticsData
							key={fieldToRender.fieldId}
							attributes={{
								fieldType: fieldToRender.picker.fieldType,
								searchTemplate: fieldToRender.picker.searchTemplate,
								refinementType: fieldToRender.picker.pickerType,
								propType: fieldToRender.picker.propType,
							}}
						>
							<Field
								key={fieldToRender.fieldId}
								fieldToRender={fieldToRender}
								onSearchWrapper={onSearchWrapper}
								onSwitchToJql={onSwitchToJql}
								fieldsToRender={fieldsToRender}
								selectedFields={selectedFields}
								onClearPicker={onFieldsUpdate}
								loading={loading}
								hydratedData={hydratedData}
								projectOptions={projectOptions}
								singleSelectFields={singleSelectFields}
								shouldUseListMarkup={shouldUseListMarkup}
								hideOperatorDropdown={hideOperatorDropdown}
								contextJql={contextJql}
								viewContext={viewContext}
								scope={scope}
								suggestedUsersCount={suggestedUsersCount}
							/>
						</ContextualAnalyticsData>
					))}
					<FieldWrapper>
						<AddFilter
							NoOptionComponent={NoOptionComponent}
							value={selectedFields}
							fieldsToRender={fieldsToRender}
							selectedFields={selectedFields}
							onSearchWrapper={onSearchWrapper}
							onClearValues={onClearAddFilterValues}
							onChange={onFieldsUpdate}
							excludeFields={requiredFields}
							contextJql={contextJql}
							viewContext={viewContext}
							scope={scope}
						/>
					</FieldWrapper>
					{showOrderByPicker && (
						<FieldWrapper>
							<OrderByPicker
								ast={ast}
								onChange={onSearchWrapper}
								contextJql={contextJql}
								hydrationData={fieldsHydrationData}
								viewContext={viewContext}
								scope={scope}
							/>
						</FieldWrapper>
					)}

					{isShortcutsDialogIntegrationAvailable && (
						<AsyncRegisterSearchCriteriaToShortcutsDialog
							fieldsToRender={fieldsToRender}
							selectedFields={selectedFields}
							onChange={onFieldsUpdate}
						/>
					)}
				</ActivePopupKeyContainer>
			)}
		</JQLBuilderContainer>
	);
};

export const JQLEditorUIWithErrorBoundaryOld = (props: JQLEditorUIOldProps): React.JSX.Element => {
	const [ast] = useAST();
	const getErrorAttributes = useCallback(
		(error: Error): AnalyticsAttributes => ({
			...isLoaderErrorAttributes(error),
			anonymizedJql: ast.query ? ast.query.accept(jqlAnonymizerVisitor) : undefined,
		}),
		[ast.query],
	);

	return (
		<JSErrorBoundary
			id="ui.jql-builder-basic.jql-editor.error-boundary"
			packageName={PACKAGE_NAME}
			teamName={TEAM_NAME}
			sendToPrivacyUnsafeSplunk
			attributes={getErrorAttributes}
			fallback={() => <JQLBuilderErrorBoundryFallback />}
		>
			<JQLEditorUIOld {...props} />
		</JSErrorBoundary>
	);
};

const JQLEditorUI = ({
	query,
	onSearch,
	onSwitchToJql,
	excludedFields = [],
	onReady,
	onJourneyStart,
	NoOptionComponent,
	AddFilterButtonComponent,
	hydrationData,
	isHydrating,
	projectOptions,
	singleSelectFields,
	shouldUseListMarkup,
	hideOperatorDropdown = false,
	irremovableFields,
	hideTextSearchInput = false,
	searchPlaceholder,
	showOrderByPicker = false,
	contextJql,
	viewContext,
	scope = null,
	suggestedUsersCount,
	onReset,
}: JQLEditorUIProps) => {
	const onPerformanceMark = usePerformanceContext();

	useLayoutEffect(() => {
		onPerformanceMark({
			mark: JQL_BUILDER_PERFORMANCE_MARKS.JQL_BUILDER_BASIC_JS_END,
		});
	}, [onPerformanceMark]);

	const fieldsHydrationData: JQLEditorUIWithErrorBoundary$data =
		useFragment<JQLEditorUIWithErrorBoundary$key>(JQL_EDITOR_HYDRATION_FRAGMENT, hydrationData);

	const loading = isHydrating ?? false;

	useLayoutEffect(() => {
		if (loading === false) {
			onReady && onReady();
		}
	}, [loading, onReady]);

	const intl = useIntl();
	const [ast] = useAST();
	const textSearchInputClauseNew = useTextSearchInputClause(ast, getTextSearchInputField());
	const [textSearchInputClauseOld] = useTextSearchInputClauseOld();

	const textSearchInputClause = fg('shplxi_628_cleanup')
		? textSearchInputClauseNew
		: textSearchInputClauseOld;

	const [{ astClauseOrder, clausesByFieldId, secondaryClausesByFieldId }] = useSearchState();
	const { isShortcutsDialogIntegrationAvailable } = useIntegrateDotDialogContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const verticalMode = useVerticalMode();

	const { internalState, setInternalState } = useFieldsToRender({
		query,
		loading,
		fieldsHydrationData,
		excludedFields,
		irremovableFields,
	});

	const onSearchWrapper: OnSearchWrapper = useCallback(
		(jql) => {
			if (onSearch) {
				setInternalState({
					internalJql: jql,
				});
				onSearch(jql);
			}
		},
		[onSearch, setInternalState],
	);

	const onFieldsUpdate = useCallback(
		({
			fieldsToRender,
			selectedFields,
		}: {
			fieldsToRender?: FieldToRender[];
			selectedFields?: FieldOption[];
		}) => {
			if (fieldsToRender !== undefined || selectedFields !== undefined) {
				setInternalState({
					...(fieldsToRender !== undefined ? { fieldsToRender } : {}),
					...(selectedFields !== undefined ? { selectedFields } : {}),
				});
			}
		},
		[setInternalState],
	);

	// Required fields are those that are never modified by the More dropdown (AddFilter component).
	// The More dropdown only manages optional fields.
	// Required fields:
	// - Cannot be added from the More dropdown
	// - Cannot be cleared from the More dropdown, if they are in the query, they remain in the query
	const requiredFields = useMemo(
		() => new Set<string>([...irremovableFields.keys(), ...(excludedFields ?? [])]),
		[excludedFields, irremovableFields],
	);

	const onClearAddFilterValues = useCallback(() => {
		const { fieldsToRender, optionalFieldsCount } = gatherFields({
			selectedFields: [],
			excludedFields,
			intl,
			fieldsHydratedData: fieldsHydrationData,
			irremovableFields,
			astClauseOrder,
			clausesByFieldId,
			secondaryClausesByFieldId,
		});

		setInternalState({
			selectedFields: [],
			fieldsToRender,
			optionalFieldsCount,
		});

		onSearchWrapper(removeOptionalFieldsFromJql(ast, requiredFields, textSearchInputClause));
	}, [
		ast,
		excludedFields,
		intl,
		fieldsHydrationData,
		irremovableFields,
		astClauseOrder,
		clausesByFieldId,
		secondaryClausesByFieldId,
		setInternalState,
		onSearchWrapper,
		requiredFields,
		textSearchInputClause,
	]);

	const { showLoadingState, fieldsToRender, selectedFields, optionalFieldsCount } = internalState;

	const StyledListItemChildComponent = isVisualRefreshEnabled()
		? ListItemChild
		: StyledListItemChild;

	const FieldWrapper = shouldUseListMarkup ? StyledListItemChildComponent : StyledChild;

	const orderByFieldCount = useMemo(
		() => (showOrderByPicker ? getAstSortFields(ast).length : undefined),
		[ast, showOrderByPicker],
	);

	return (
		<JQLBuilderContainer>
			{showLoadingState ? (
				<JQLEditorLoading
					fieldsToRender={fieldsToRender}
					optionalFieldsCount={optionalFieldsCount}
					shouldUseListMarkup={shouldUseListMarkup}
					hideTextSearchInput={hideTextSearchInput}
					searchPlaceholder={searchPlaceholder}
					showOrderByPicker={showOrderByPicker}
					orderByFieldsCount={orderByFieldCount}
				/>
			) : (
				<ActivePopupKeyContainer
					createAnalyticsEvent={createAnalyticsEvent}
					onJourneyStart={onJourneyStart}
				>
					{!hideTextSearchInput && (
						<ContextualAnalyticsData
							attributes={{
								fieldType: getTextSearchInputField(),
								searchTemplate: getTextSearchInputField(),
								refinementType: PickerTypes.Text,
								propType: GenericPropTypes.NoProp,
							}}
						>
							<TextSearchInput
								onSearchWrapper={onSearchWrapper}
								onJourneyStart={onJourneyStart}
								shouldUseListMarkup={shouldUseListMarkup}
								placeholder={searchPlaceholder}
							/>
						</ContextualAnalyticsData>
					)}
					{fieldsToRender.map((fieldToRender) => (
						<ContextualAnalyticsData
							key={fieldToRender.fieldId}
							attributes={{
								fieldType: fieldToRender.picker.fieldType,
								searchTemplate: fieldToRender.picker.searchTemplate,
								refinementType: fieldToRender.picker.pickerType,
								propType: fieldToRender.picker.propType,
							}}
						>
							<Field
								key={fieldToRender.fieldId}
								fieldToRender={fieldToRender}
								onSearchWrapper={onSearchWrapper}
								onSwitchToJql={onSwitchToJql}
								fieldsToRender={fieldsToRender}
								selectedFields={selectedFields}
								onClearPicker={onFieldsUpdate}
								loading={loading}
								hydratedData={hydrationData}
								projectOptions={projectOptions}
								singleSelectFields={singleSelectFields}
								shouldUseListMarkup={shouldUseListMarkup}
								hideOperatorDropdown={hideOperatorDropdown}
								contextJql={contextJql}
								viewContext={viewContext}
								scope={scope}
								suggestedUsersCount={suggestedUsersCount}
							/>
						</ContextualAnalyticsData>
					))}
					<FieldWrapper>
						{verticalMode ? (
							<Box
								xcss={buttonsLineStyles}
								testId="jql-builder-basic.ui.jql-editor.jql-editor-filter-wrapper-actions"
							>
								<AddFilter
									NoOptionComponent={NoOptionComponent}
									ButtonComponent={AddFilterButtonComponent}
									value={selectedFields}
									fieldsToRender={fieldsToRender}
									selectedFields={selectedFields}
									onSearchWrapper={onSearchWrapper}
									onClearValues={onClearAddFilterValues}
									onChange={onFieldsUpdate}
									excludeFields={requiredFields}
									contextJql={contextJql}
									viewContext={viewContext}
									scope={scope}
								/>
								{fieldsToRender.length > 1 && onReset ? <ResetButton onReset={onReset} /> : null}
							</Box>
						) : (
							<AddFilter
								NoOptionComponent={NoOptionComponent}
								ButtonComponent={AddFilterButtonComponent}
								value={selectedFields}
								fieldsToRender={fieldsToRender}
								selectedFields={selectedFields}
								onSearchWrapper={onSearchWrapper}
								onClearValues={onClearAddFilterValues}
								onChange={onFieldsUpdate}
								excludeFields={requiredFields}
								contextJql={contextJql}
								viewContext={viewContext}
								scope={scope}
							/>
						)}
					</FieldWrapper>
					{showOrderByPicker && (
						<FieldWrapper>
							<OrderByPicker
								ast={ast}
								onChange={onSearchWrapper}
								contextJql={contextJql}
								hydrationData={fieldsHydrationData}
								viewContext={viewContext}
								scope={scope}
							/>
						</FieldWrapper>
					)}

					{isShortcutsDialogIntegrationAvailable && (
						<AsyncRegisterSearchCriteriaToShortcutsDialog
							fieldsToRender={fieldsToRender}
							selectedFields={selectedFields}
							onChange={onFieldsUpdate}
						/>
					)}
				</ActivePopupKeyContainer>
			)}
		</JQLBuilderContainer>
	);
};

const JQLEditorUIWithErrorBoundary = (props: JQLEditorUIProps): React.JSX.Element => {
	const [ast] = useAST();
	const getErrorAttributes = useCallback(
		(error: Error): AnalyticsAttributes => ({
			...isLoaderErrorAttributes(error),
			anonymizedJql: ast.query ? ast.query.accept(jqlAnonymizerVisitor) : undefined,
		}),
		[ast.query],
	);

	return (
		<JSErrorBoundary
			id="ui.jql-builder-basic.jql-editor.error-boundary"
			packageName={PACKAGE_NAME}
			teamName={TEAM_NAME}
			sendToPrivacyUnsafeSplunk
			attributes={getErrorAttributes}
			fallback={() => <JQLBuilderErrorBoundryFallback />}
		>
			<JQLEditorUI {...props} />
		</JSErrorBoundary>
	);
};

const buttonsLineStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
});

export default JQLEditorUIWithErrorBoundary;
